import styled from '@emotion/styled'
import { Button, notification } from 'antd'
import BigNumber from 'bignumber.js'
import Slider from 'rc-slider'
import { useEffect, useMemo, useState } from 'react'
import { useAccount, useConnect, useBalance } from 'wagmi'
import { injected } from 'wagmi/connectors'

import error_icon from '@/assets/error_icon.png'
import wanIcon from '@/assets/wanIcon.svg'
import xwanIcon from '@/assets/xwanIcon.svg'
import XWANWrapConfirmModal from '@/components/xwan-wrap-confirm-modal'
import { getDecimal } from '@/config'
import { useLocalStorage } from '@/context/localstorage-context'
import type { HistoryItemType, RedeemListType } from '@/types/xwan-swap-type'
import { formatBalanceAddPer, formatBalance, isNumber } from '@/utils'
import { getAllByKey, add } from '@/utils/db'
import { useQueryTokenPrice } from '@/utils/queries'
import { useXWanSwapFunc, useErc20NewContract } from '@/utils/useWeb3'
import {
  readUserRedeemLength,
  readUserRedeems,
  readWanAmountByVestingDuration,
  writeSwapDeposit,
  writeSwapRedeem,
  readStakeXWANBalance,
  writeSwapCancelRedeem,
  writeSwapFinalizeRedeem,
} from '@/utils/xwanUseContract'

import 'rc-slider/assets/index.css'

console.log('version::::    v0.0.10 ')

const InputCon = ({
  type,
  token,
  inpTxt,
  inpChange,
  price,
}: {
  type: string
  token: string
  inpTxt: string
  inpChange?: (txt: any) => void
  price: string
}) => {
  const tokenTxt = useMemo(() => {
    if (token === 'wan') {
      return 'WAN'
    } else {
      return 'xWAN'
    }
  }, [token])

  const usPrice = useMemo(() => {
    const value = new BigNumber(inpTxt).times(price)
    if (value.isNaN()) {
      return '0'
    } else {
      return value.toFormat(2)
    }
  }, [inpTxt, price])

  const iconComponent = useMemo(() => {
    if (token === 'wan') {
      return wanIcon
    } else {
      return xwanIcon
    }
  }, [token])

  return (
    <InpContainer className="bg-light-555 dark:bg-dark-1111">
      <LeftCon>
        {type === 'input' ? (
          <Input
            type="number"
            className="text-light-0 dark:text-white"
            placeholder="0"
            value={inpTxt}
            onChange={inpChange}
          />
        ) : (
          <ReadInput className="text-light-0 dark:text-white">
            {inpTxt || '0'}
          </ReadInput>
        )}
        <UsPrice className="text-light-1666 dark:text-dark-1222">
          ${usPrice}
        </UsPrice>
      </LeftCon>
      <RightPaddingCon>
        <RightCon className="bg-white dark:bg-dark-1999 text-light-0 dark:text-white">
          <TokenIcon src={iconComponent}></TokenIcon>
          {tokenTxt}
        </RightCon>
      </RightPaddingCon>
    </InpContainer>
  )
}

const SwapXWAN = () => {
  const { theme } = useLocalStorage()
  const isdark = useMemo(() => theme === 'dark', [theme])
  const { data: tokensPrice } = useQueryTokenPrice()
  const { connect } = useConnect()
  const { address } = useAccount()
  const connected = useMemo(() => {
    return address !== void 0
  }, [address])
  const { data, status } = useBalance({ address })
  // get data
  const [depositLength, setDepositLength] = useState(0)
  const [depositList, setDepositList] = useState<RedeemListType>([])
  const [redeemLength, setRedeemLength] = useState(0)
  const [redeemResList, setRedeemResList] = useState<RedeemListType>([])
  const [, swapContract] = useXWanSwapFunc()
  const [showWrapConfirmModal, setShowWrapConfirmModal] = useState(false)
  const [, xWanContractAddr] = useErc20NewContract('xWAN')
  const [unwrapBalance, setUnwrapBalance] = useState('N/A')
  const [isRefresh, setIsRefresh] = useState(0)

  const wanBalance = useMemo(() => {
    return data && status === 'success'
      ? new BigNumber(data.value).toString(10)
      : 'N/A'
  }, [data, status, isRefresh])
  // input
  const [lockDays, setLockDays] = useState(90)
  const [wrapInp, setWrapInp] = useState('0')
  const [unwrapInp, setUnwrapInp] = useState('0')
  const [unwrapReceiveMap, setUnwrapReceiveMap] = useState(new Map())
  // loading
  const [wrapLoading, setWrapLoading] = useState(false)
  const [unwrapLoading, setUnwrapLoading] = useState(false)
  const [cancelLoadingArr, setCancelLoadingArr] = useState<number[]>([])
  const [claimLoadingArr, setClaimLoadingArr] = useState<number[]>([])
  // view more
  const [showWrapViewMore, setShowWrapViewMore] = useState(false)
  const [showUnwrapViewMore, setShowUnwrapViewMore] = useState(false)

  // history
  const getHistory = async (name: string) => {
    try {
      if (!address) return []
      const dbList: HistoryItemType[] = await getAllByKey(name, 'user', address)
      return dbList
    } catch (e) {
      console.error(e)
      return []
    }
  }

  const addHistory = async (name: string, value: HistoryItemType) => {
    const item = await add(name, value)
    return item
  }

  // wrap history
  useEffect(() => {
    const fn = async () => {
      if (!address) return
      let list = await getHistory('wrap')
      list = list.sort(
        (a: HistoryItemType, b: HistoryItemType) =>
          Number(b.startTime) - Number(a.startTime),
      )
      console.log('get wrap history', list)
      setDepositLength(list.length)
      setDepositList(list)
    }
    fn()
  }, [isRefresh, address])

  const depositHitoryList = useMemo(() => {
    let list
    const arr = JSON.parse(JSON.stringify(depositList))
    if (arr.length > 3 && !showWrapViewMore) {
      list = arr.splice(0, 3)
    } else {
      list = arr
    }
    return list
  }, [depositList, showWrapViewMore])
  // unwrap history
  useEffect(() => {
    const fn = async () => {
      try {
        const addr = address as `0x${string}`
        const len = await readUserRedeemLength(addr, swapContract)
        setRedeemLength(len)
        const res = await Promise.all(
          Array.from({ length: Number(len?.toString()) }, async (_, index) => {
            let data = null
            try {
              data = await readUserRedeems(addr, index, swapContract)
            } catch (e) {
              console.error(e)
            }
            return data
          }),
        )
        const list: RedeemListType = res
          .filter((v) => v)
          .map((value, key) => {
            const item = value as {
              0: string
              1: string
              2: string
              3: string
              endTime: string
              startTime: string
              wanAmount: string
              xWanAmount: string
            }
            const decimal = getDecimal.get('WAN')
            return {
              id: key,
              amountIn: formatBalance(item[0], decimal),
              amountOut: formatBalance(item[1], decimal),
              startTime: item[2],
              endTime: item[3],
              endInDays: Math.ceil(
                (Number(item[3]) - Date.now() / 1000) / 86400,
              ),
            }
          })
          .sort((a, b) => Number(b.startTime) - Number(a.startTime))
        setRedeemResList(list)
      } catch (e) {
        console.error(e)
      }
    }
    if (address) fn()
  }, [isRefresh, address])

  const redeemHitoryList = useMemo(() => {
    let list
    const arr = JSON.parse(JSON.stringify(redeemResList))
    if (arr.length > 3 && !showUnwrapViewMore) {
      list = arr.splice(0, 3)
    } else {
      list = arr
    }
    return list
  }, [redeemResList, showUnwrapViewMore])

  const handleViewMore = (type: string) => {
    if (type === 'wrap') {
      setShowWrapViewMore(true)
    } else {
      setShowUnwrapViewMore(true)
    }
  }

  // swap
  useEffect(() => {
    const fn = async () => {
      const unwrapMap = new Map(unwrapReceiveMap)
      try {
        if (!unwrapInp || unwrapInp === '0') {
          unwrapMap.set(`0-${lockDays}`, '0')
        } else {
          const xWANDecimal = getDecimal.get('xWAN') || 0
          const amountNum = new BigNumber(unwrapInp)
            .times(Math.pow(10, xWANDecimal))
            .toString(10)
          const time = lockDays * 86400
          let value = await readWanAmountByVestingDuration(
            amountNum,
            time,
            swapContract,
          )
          value = formatBalance(value, xWANDecimal)
          unwrapMap.set(`${unwrapInp}-${lockDays}`, value)
        }
        setUnwrapReceiveMap(unwrapMap)
      } catch (e) {
        console.error(e)
      }
    }
    fn()
  }, [unwrapInp, lockDays])

  const checkInpAmount = (inpValue: string, decimal: number) => {
    const value: string = inpValue
    if (!isNumber(Number(value))) {
      return false
    }
    if (Number(value) <= 0) {
      return false
    }
    const arr = value.split('.')
    if (arr[1] && arr[1].length > decimal) {
      return false
    }
    return true
  }

  const wrapReceiveInp = useMemo(() => {
    const value = unwrapReceiveMap.get(`${unwrapInp}-${lockDays}`)
    return value ? value : '0'
  }, [unwrapInp, unwrapReceiveMap, lockDays])

  const handleWrapInp = (e: any) => {
    let value = e.target.value
    if (!isNumber(value)) {
      return
    }
    if (value < 0) {
      return
    }
    const arr = value.split('')
    if (arr.length > 1 && arr[0] === '0' && arr[1] !== '.') {
      value = String(Number(value))
    }
    setWrapInp(value)
  }

  const handleWrap = async () => {
    try {
      if (!address) return
      setShowWrapConfirmModal(false)
      setWrapLoading(true)
      const decimal = getDecimal.get('WAN') || 0
      if (!checkInpAmount(wrapInp, decimal)) {
        notification.error({
          message: 'Error',
          description: 'Invalid Amount.',
          className: `${isdark ? 'noticeSuccess_dark' : 'noticeSuccess_light'}`,
          icon: <img width={30} src={error_icon} alt="notification" />,
        })
        setWrapLoading(false)
        return
      }
      const amountNum = new BigNumber(wrapInp)
        .times(Math.pow(10, decimal))
        .toString(10)
      await writeSwapDeposit(amountNum, swapContract)
      const time = String(new Date().getTime())
      await addHistory('wrap', {
        id: depositLength,
        amountIn: wrapInp,
        amountOut: wrapInp,
        startTime: time,
        endTime: time,
        endInDays: 0,
        user: address,
        status: 'success',
      })
      setIsRefresh(isRefresh + 1)
      setWrapLoading(false)
      setWrapInp('0')
    } catch (e) {
      setWrapLoading(false)
      console.error(e)
    }
  }

  // unwrap
  const handleUnwrapInp = (e: any) => {
    let value = e.target.value
    if (!isNumber(value)) {
      return
    }
    if (value < 0) {
      return
    }
    const arr = value.split('')
    if (arr.length > 1 && arr[0] === '0' && arr[1] !== '.') {
      value = String(Number(value))
    }
    setUnwrapInp(value)
  }

  const handleUnwrap = async () => {
    try {
      setUnwrapLoading(true)
      const xWANDecimal = getDecimal.get('xWAN') || 0
      if (!checkInpAmount(unwrapInp, xWANDecimal)) {
        notification.error({
          message: 'Error',
          description: 'Invalid Amount.',
          className: `${isdark ? 'noticeSuccess_dark' : 'noticeSuccess_light'}`,
          icon: <img width={30} src={error_icon} alt="notification" />,
        })
        setUnwrapLoading(false)
        return
      }
      const amountNum = new BigNumber(unwrapInp)
        .times(Math.pow(10, xWANDecimal))
        .toString(10)
      const time = lockDays * 86400
      // await client.methods.redeem(amountNum, time).send({ from: address })
      await writeSwapRedeem(amountNum, time, swapContract)
      setIsRefresh(isRefresh + 1)
      setUnwrapLoading(false)
      setUnwrapInp('0')
    } catch (e) {
      setUnwrapLoading(false)
      console.error(e)
    }
  }
  // balance
  // useEffect(() => {
  //   const fn = async () => {
  //     if (!address) return;
  //     try {
  //       const balance = await web3.eth
  //         .getBalance(address)
  //         .then((balance: string) => balance);
  //       setWanBalance(balance);
  //     } catch (e) {
  //       console.error("WANBalance error", e);
  //     }
  //   };
  //   fn();
  // }, [isRefresh, address, web3]);

  useEffect(() => {
    const fn = async () => {
      if (!address) return
      try {
        const balance = await readStakeXWANBalance(address, xWanContractAddr)
        setUnwrapBalance(balance)
      } catch (e) {
        console.log('xWANBalance error', e)
      }
    }
    fn()
  }, [isRefresh, address, xWanContractAddr])

  const xWANPrice = useMemo(() => {
    return tokensPrice?.wanchain?.usd || '0'
  }, [tokensPrice])

  const handleMax = (type: string) => {
    const decimal = getDecimal.get('WAN')
    if (type === 'wrap') {
      const saveAmount = new BigNumber(0.001).times(
        Math.pow(10, Number(decimal)),
      )
      const maxAmount = new BigNumber(wanBalance).minus(saveAmount).toString(10)
      const value = formatBalance(maxAmount, decimal)
      setWrapInp(value)
    } else {
      const value = formatBalance(unwrapBalance, decimal)
      setUnwrapInp(value)
    }
  }

  const handleCancel = async (item: HistoryItemType, index: number) => {
    let arr = JSON.parse(JSON.stringify(cancelLoadingArr))
    try {
      arr.push(index)
      setCancelLoadingArr(arr)
      // await client.methods
      //   .cancelRedeem(item.id)
      //   .send({ from: address, value: 0 })
      await writeSwapCancelRedeem(item.id, swapContract)
      arr = JSON.parse(JSON.stringify(cancelLoadingArr))
      const key = arr.findIndex((v: number) => v === index)
      arr.splice(key, 1)
      setCancelLoadingArr(arr)
      setIsRefresh(isRefresh + 1)
    } catch (e) {
      arr = JSON.parse(JSON.stringify(cancelLoadingArr))
      const key = arr.findIndex((v: number) => v === index)
      arr.splice(key, 1)
      setCancelLoadingArr(arr)
      console.error(e)
    }
  }

  const handleClaim = async (item: HistoryItemType, index: number) => {
    let arr = JSON.parse(JSON.stringify(claimLoadingArr))
    try {
      arr.push(index)
      setClaimLoadingArr(arr)
      // await client.methods
      //   .finalizeRedeem(item.id)
      //   .send({ from: address, value: 0 })
      await writeSwapFinalizeRedeem(item.id, swapContract)
      setIsRefresh(isRefresh + 1)
      arr = JSON.parse(JSON.stringify(claimLoadingArr))
      const key = arr.findIndex((v: number) => v === index)
      arr.splice(key, 1)
      setClaimLoadingArr(arr)
    } catch (e) {
      arr = JSON.parse(JSON.stringify(claimLoadingArr))
      const key = arr.findIndex((v: number) => v === index)
      arr.splice(key, 1)
      setClaimLoadingArr(arr)
      console.error(e)
    }
  }

  return (
    <Container>
      <Title className="text-light-0 dark:text-white">Wrap & Unwrap</Title>
      <Description className="text-light-0 dark:text-white">
        Wrap WAN to receive xWAN instantly, or unwrap within the 0–90 day linear
        vesting period to redeem WAN, from 1:0.25 at day 0 to 1:1 at day 90.
      </Description>
      <div className="grid md:grid-cols-1 grid-cols-2 gap-6">
        {/* wrap */}
        <SwapItemCon className="bg-light-1444 dark:bg-dark-222">
          <SwapItemTitle className="text-light-0 dark:text-white">
            Wrap WAN
          </SwapItemTitle>
          <SwapInfoCon className="bg-white dark:bg-dark-1999">
            <SwapInfoPaddingCon>
              <SwapInfoTitle>
                <SwapInfoTitleName className="text-light-1555">
                  Wrap Amount
                </SwapInfoTitleName>
                <BalanceInfoCon className="dark:text-white">
                  <WalletIcon>
                    <svg
                      width="14"
                      height="12"
                      viewBox="0 0 14 12"
                      fill={isdark ? '#fff' : '#333'}
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M10.667 2.667h2a.667.667 0 0 1 .666.666v8a.667.667 0 0 1-.666.667h-12A.666.666 0 0 1 0 11.333V.667A.667.667 0 0 1 .667 0h10v2.667zM1.333 4v6.667H12V4H1.333zm0-2.667v1.334h8V1.333h-8zm7.334 5.334h2V8h-2V6.667z" />
                    </svg>
                  </WalletIcon>
                  &nbsp;
                  {wanBalance === 'N/A'
                    ? 'N/A'
                    : formatBalanceAddPer(wanBalance, getDecimal.get('WAN'), 2)}
                  &nbsp;
                  <span
                    className="text-light-222 dark:text-dark-111 cursor-pointer"
                    onClick={() => handleMax('wrap')}
                  >
                    MAX
                  </span>
                </BalanceInfoCon>
              </SwapInfoTitle>
              <InputCon
                type="input"
                token="wan"
                inpTxt={wrapInp}
                inpChange={handleWrapInp}
                price={xWANPrice}
              ></InputCon>
              <SwapIconCon>
                <SwapIcon className="bg-light-555 dark:bg-dark-1111">
                  <svg
                    width="17"
                    height="26"
                    viewBox="0 0 17 26"
                    fill={isdark ? '#2fbdf4' : '#0f68aa'}
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M8.5 25.5 17 17h-6.375V0h-4.25v17H0" />
                  </svg>
                </SwapIcon>
              </SwapIconCon>
              <ReceiveCon className="text-light-1555 dark:text-dark-1000">
                You will receive
              </ReceiveCon>
              <InputCon
                type="readonly"
                token="xwan"
                inpTxt={wrapInp}
                price={xWANPrice}
              ></InputCon>
              <ButtonPaddingCon>
                {!connected ? (
                  <LineBtn
                    className="bg-light-111"
                    onClick={() => connect({ connector: injected() })}
                  >
                    Connect Wallet
                  </LineBtn>
                ) : (
                  <LineBtn
                    className="bg-light-111"
                    onClick={() => setShowWrapConfirmModal(true)}
                    loading={wrapLoading}
                  >
                    Wrap
                  </LineBtn>
                )}
              </ButtonPaddingCon>
            </SwapInfoPaddingCon>
            {depositLength > 0 ? (
              <>
                <Line className="bg-light-555 dark:bg-dark-1666"></Line>
                <SwapInfoPaddingCon>
                  <HistoryTitle className="text-light-0 dark:text-white">
                    Wrap History
                  </HistoryTitle>
                  {depositHitoryList.map((v: HistoryItemType, k: number) => (
                    <HistoryItemCon
                      key={k}
                      className="border-solid border border-light-555 dark:border-dark-1666"
                    >
                      <div className="flex-1">
                        <HistoryWrapInfoCon className="text-light-0 dark:text-white">
                          <div className="text-light-222 dark:text-dark-111">
                            {formatBalanceAddPer(v.amountIn, 0, 2)}
                          </div>
                          &nbsp;WAN
                          <ArrowDirection className="bg-light-1888 dark:bg-dark-1333">
                            <svg
                              width="6"
                              height="9"
                              viewBox="0 0 6 9"
                              fill="#2FBDF4"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path d="M3.5 4.5 0 8l1 1 4.5-4.5L1 0 0 1l3.5 3.5z" />
                            </svg>
                          </ArrowDirection>
                          <div className="text-light-222 dark:text-dark-111">
                            {formatBalanceAddPer(v.amountOut, 0, 2)}
                          </div>
                          &nbsp;xWAN
                        </HistoryWrapInfoCon>
                      </div>
                      <StatusBtn
                        className="border border-solid border-light-555 dark:border-dark-1666 text-light-1777"
                        allowclick={String(false)}
                      >
                        Success
                      </StatusBtn>
                    </HistoryItemCon>
                  ))}
                  {depositHitoryList.length !== 3 ||
                  Number(depositLength) === 3 ? null : (
                    <ViewMoreButton
                      className="border border-solid border-light-555 dark:border-dark-1666 text-light-0 dark:text-white"
                      onClick={() => handleViewMore('wrap')}
                    >
                      View More
                    </ViewMoreButton>
                  )}
                </SwapInfoPaddingCon>
              </>
            ) : null}
          </SwapInfoCon>
        </SwapItemCon>
        {/* unwrap */}
        <SwapItemCon className="bg-light-1444 dark:bg-dark-222">
          <SwapItemTitle className="text-light-0 dark:text-white">
            Unwrap xWAN
          </SwapItemTitle>
          <SwapInfoCon className="bg-white dark:bg-dark-1999">
            <SwapInfoPaddingCon>
              <SwapInfoTitle>
                <SwapInfoTitleName className="text-light-1555">
                  Unwrap Amount
                </SwapInfoTitleName>
                <BalanceInfoCon className="dark:text-white">
                  <WalletIcon>
                    <svg
                      width="14"
                      height="12"
                      viewBox="0 0 14 12"
                      fill={isdark ? '#fff' : '#333'}
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M10.667 2.667h2a.667.667 0 0 1 .666.666v8a.667.667 0 0 1-.666.667h-12A.666.666 0 0 1 0 11.333V.667A.667.667 0 0 1 .667 0h10v2.667zM1.333 4v6.667H12V4H1.333zm0-2.667v1.334h8V1.333h-8zm7.334 5.334h2V8h-2V6.667z" />
                    </svg>
                  </WalletIcon>
                  &nbsp;
                  {unwrapBalance === 'N/A'
                    ? 'N/A'
                    : formatBalanceAddPer(
                        unwrapBalance,
                        getDecimal.get('xWAN'),
                        2,
                      )}
                  &nbsp;
                  <span
                    className="text-light-222 dark:text-dark-111 cursor-pointer"
                    onClick={() => handleMax('unwrap')}
                  >
                    MAX
                  </span>
                </BalanceInfoCon>
              </SwapInfoTitle>
              <InputCon
                type="input"
                token="xwan"
                inpTxt={unwrapInp}
                inpChange={handleUnwrapInp}
                price={xWANPrice}
              ></InputCon>
              <LockCon>
                <LockTxtCon className="text-light-0 dark:text-white">
                  Lock Time: {lockDays} {lockDays === 1 ? 'day' : 'days'}
                </LockTxtCon>
                <LockTimeCon>
                  <Slider
                    min={0}
                    max={90}
                    marks={{
                      0: 'Now',
                      30: '30',
                      60: '60',
                      90: '90',
                    }}
                    value={lockDays}
                    onChange={(v: number | number[]) => {
                      if (Array.isArray(v)) {
                        setLockDays(v[0])
                      } else {
                        setLockDays(v)
                      }
                    }}
                  />
                </LockTimeCon>
              </LockCon>
              <ReceiveCon className="text-light-1555 dark:text-dark-1000">
                You will receive
              </ReceiveCon>
              <InputCon
                type="readonly"
                token="wan"
                inpTxt={wrapReceiveInp}
                price={xWANPrice}
              ></InputCon>
              <ButtonPaddingCon>
                {!connected ? (
                  <LineBtn
                    className="bg-light-111"
                    onClick={() => connect({ connector: injected() })}
                  >
                    Connect Wallet
                  </LineBtn>
                ) : (
                  <LineBtn
                    className="bg-light-111"
                    onClick={handleUnwrap}
                    loading={unwrapLoading}
                  >
                    Unwrap
                  </LineBtn>
                )}
              </ButtonPaddingCon>
            </SwapInfoPaddingCon>
            {redeemLength > 0 ? (
              <>
                <Line className="bg-light-555 dark:bg-dark-1666"></Line>
                <SwapInfoPaddingCon>
                  <HistoryTitle className="text-light-0 dark:text-white">
                    Vesting History
                  </HistoryTitle>
                  {redeemHitoryList.map((v: HistoryItemType, k: number) => (
                    <HistoryItemCon
                      key={k}
                      className="border-solid border border-light-555 dark:border-dark-1666"
                    >
                      <div className="flex-1 text-light-0 dark:text-white">
                        <HistoryWrapInfoCon className="text-light-0 dark:text-white">
                          <div className="text-light-222 dark:text-dark-111">
                            {formatBalanceAddPer(v.amountIn, 0, 2)}
                          </div>
                          &nbsp;xWAN
                          <ArrowDirection className="bg-light-1888 dark:bg-dark-1333">
                            <svg
                              width="6"
                              height="9"
                              viewBox="0 0 6 9"
                              fill="#2FBDF4"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path d="M3.5 4.5 0 8l1 1 4.5-4.5L1 0 0 1l3.5 3.5z" />
                            </svg>
                          </ArrowDirection>
                          <div className="text-light-222 dark:text-dark-111">
                            {formatBalanceAddPer(v.amountOut, 0, 2)}
                          </div>
                          &nbsp;WAN
                        </HistoryWrapInfoCon>
                        <HistoryTimeInfoCon>
                          {v.endInDays > 0 ? (
                            <>
                              Ends in&nbsp;
                              <div className="text-light-1777">
                                {v.endInDays}
                              </div>
                              &nbsp;days
                            </>
                          ) : (
                            <>Ended</>
                          )}
                        </HistoryTimeInfoCon>
                      </div>
                      {v.endInDays > 0 ? (
                        <StatusBtn
                          className="bg-light-1888 dark:bg-dark-1444 text-light-222 dark:text-dark-111"
                          allowclick={String(true)}
                          onClick={() => {
                            handleCancel(v, k)
                          }}
                          loading={cancelLoadingArr.includes(k)}
                        >
                          Cancel
                        </StatusBtn>
                      ) : (
                        <StatusBtn
                          className="bg-light-1888 dark:bg-dark-1444 text-light-222 dark:text-dark-111"
                          allowclick={String(true)}
                          onClick={() => {
                            handleClaim(v, k)
                          }}
                          loading={claimLoadingArr.includes(k)}
                        >
                          Claim
                        </StatusBtn>
                      )}
                    </HistoryItemCon>
                  ))}
                  {redeemHitoryList.length !== 3 ||
                  Number(redeemLength) === 3 ? null : (
                    <ViewMoreButton
                      className="border border-solid border-light-555 dark:border-dark-1666 text-light-0 dark:text-white"
                      onClick={() => handleViewMore('unwrap')}
                    >
                      View More
                    </ViewMoreButton>
                  )}
                </SwapInfoPaddingCon>
              </>
            ) : null}
          </SwapInfoCon>
        </SwapItemCon>
      </div>
      <XWANWrapConfirmModal
        visible={showWrapConfirmModal}
        onCancel={() => setShowWrapConfirmModal(false)}
        onConfirm={async () => handleWrap()}
      ></XWANWrapConfirmModal>
    </Container>
  )
}

export default SwapXWAN

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: 6rem 18% 2rem;
  text-align: center;
  height: 100%;

  @media (max-width: 768px) {
    margin: 0 8% 2rem;
  }
`

const Title = styled.div`
  // font-family: Inter;
  font-size: 3.2rem;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: left;
  margin-bottom: 0.8rem;
`

const Description = styled.div`
  // font-family: Inter;
  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.43;
  letter-spacing: normal;
  text-align: left;
  margin-bottom: 2.4rem;
`

const SwapItemCon = styled.div`
  border-radius: 1.6rem;
  height: fit-content;
`

const SwapItemTitle = styled.div`
  padding: 2.4rem 2.4rem 1.6rem;
  // font-family: Inter;
  font-size: 24px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1;
  letter-spacing: normal;
  text-align: left;
`

const SwapInfoPaddingCon = styled.div`
  padding: 1.6rem 2.4rem 2.4rem;
`

const ViewMoreButton = styled(Button)`
  width: 100%;
  height: 3.4rem;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 1.2rem;
  font-size: 1.4rem;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  letter-spacing: normal;
  background: none !important;

  &:hover {
    background: none !important;
  }

  &:active {
    background: none !important;
  }

  &:focus {
    background: none !important;
  }

  // span {
  //   font-family: Inter !important;
  // }
`

const SwapInfoCon = styled.div`
  border-radius: 1.6rem;
`

const SwapInfoTitle = styled.div`
  display: flex;
  justify-content: space-between;
`

const SwapInfoTitleName = styled.div`
  // font-family: Inter;
  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.71;
  letter-spacing: normal;
  text-align: left;
`

const BalanceInfoCon = styled.div`
  display: flex;
  align-items: center;
  // font-family: Inter;

  // span {
  //   font-family: Inter;
  // }
`

const WalletIcon = styled.div`
  width: 1.6rem;
  height: 1.6rem;

  svg {
    width: 1.6remm;
    height: 1.6rem;
  }
`

const InpContainer = styled.div`
  width: 100%;
  padding: 0.6rem 1.6rem 1.6rem;
  border-radius: 1.6rem;
  display: flex;
  justify-content: space-between;
`

const LeftCon = styled.div`
  flex: 1;
  width: 0;
`

const RightPaddingCon = styled.div`
  padding-top: 1rem;
  margin-left: 1.8rem;
`

const RightCon = styled.div`
  padding: 0.6rem 1.2rem;
  border-radius: 1.2rem;
  display: flex;
  align-items: center;
  // font-family: Inter;
`

const Input = styled.input`
  width: 100%;
  height: 5.2rem;
  line-height: 5.2rem;
  background: none;
  border: none;
  outline: none;
  // font-family: Inter;
  font-size: 3.6rem;
  font-weight: bold;
  text-align: left;

  &:focus {
    box-shadow: none;
  }
`

const ReadInput = styled.div`
  width: 100%;
  height: 5.2rem;
  line-height: 5.2rem;
  // font-family: Inter;
  font-size: 3.6rem;
  font-weight: bold;
  text-align: left;
  overflow: hidden;
`

const TokenIcon = styled.img`
  width: 2.8rem;
  height: 2.8rem;
  margin-right: 0.6rem;
`

const UsPrice = styled.div`
  height: 2rem;
  line-height: 2rem;
  font-size: 1.4rem;
  font-weight: 500;
  font-stretch: normal;
  font-style: normal;
  letter-spacing: normal;
  text-align: left;
  // font-family: Inter;
`

const SwapIconCon = styled.div`
  padding: 2.6rem 0 1.6rem;
  display: flex;
  justify-content: center;
`

const SwapIcon = styled.div`
  width: 5.6rem;
  height: 5.6rem;
  border-radius: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`

const LockCon = styled.div`
  padding-top: 1.6rem;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const LockTxtCon = styled.div`
  width: 100%;
  height: 2.2rem;
  line-height: 2.2rem;
  font-size: 2rem;
  text-align: left;
  margin-bottom: 1rem;
`

const LockTimeCon = styled.div`
  width: 94%;
  height: 5rem;
`

const ReceiveCon = styled.div`
  height: 2.4rem;
  line-height: 2.4rem;
  font-size: 1.4rem;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  letter-spacing: normal;
  text-align: left;
  margin-bottom: 0.4rem;
  // font-family: Inter;
`

const ButtonPaddingCon = styled.div`
  padding-top: 1.6rem;
`

const ButtonCon = styled(Button)`
  border: none;
  height: fit-content;

  span {
    // font-family: Inter !important;
  }
`

const LineBtn = styled(ButtonCon)`
  width: 100%;
  padding: 1.2rem 0 !important;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.6rem;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: center;
  color: #fff;
  border-radius: 8px;
`

const Line = styled.div`
  width: 100%;
  height: 1px;
`

const HistoryTitle = styled.div`
  padding: 0.5rem 0;
  // font-family: Inter;
  font-size: 1.4rem;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  letter-spacing: normal;
  text-align: left;
  margin-bottom: 0.4rem;
`

const HistoryItemCon = styled.div`
  padding: 0 1.2rem;
  height: 6.6rem;
  border-radius: 0.8rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.4rem;
`

const HistoryWrapInfoCon = styled.div`
  display: flex;
  align-items: center;
  // font-family: Inter;
  font-size: 1.6rem;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  letter-spacing: normal;
  text-align: left;
`

const ArrowDirection = styled.div`
  width: 1.6rem;
  height: 1.6rem;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 0.4rem;
  border-radius: 0.2rem;

  svg {
    width: 0.5rem;
    height: 0.8rem;
  }
`

const HistoryTimeInfoCon = styled.div`
  display: flex;
  align-items: center;
  padding-top: 0.4rem;
  // font-family: Inter;
  font-size: 1.2rem;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  letter-spacing: normal;
  text-align: left;
`

const StatusBtn = styled(Button)<{
  allowclick: string
}>`
  background: none;
  width: 8rem;
  height: 4.4rem;
  border-radius: 0.8rem;
  display: flex;
  justify-content: center;
  align-items: center;
  // font-family: Inter;
  font-size: 1.4rem;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  letter-spacing: normal;
  text-align: center;
  border: none;
  cursor: ${(props) => (props.allowclick === 'true' ? 'pointer' : 'default')};

  &:hover {
    background: none !important;
  }

  &:active {
    background: none !important;
  }

  &:focus {
    background: none !important;
  }

  // span {
  //   font-family: Inter !important;
  // }
`
